import { mediaClient, ResponseData, DomainType } from "@/lib/request";
import {
  ActivityCouponResult,
  ReceiveCouponData,
  GrantRightsTab,
  WayneSupplyRecallResult,
  GrantCouponItem,
  CouponActivityConfig,
  CouponWallItem,
  TitleItem,
  CouponData,
  IdentityScene,
} from "@/types/coupon-wall";
import { ssrFetch, SSRFetchConfig } from "@/lib/ssrFetch";
import { RUNTIME_ENV_TYPE } from "@/lib/constant";

/**
 * 优惠券相关API路径
 */
const COUPON_API_PATHS = {
  // 红包墙列表
  LIST_COUPON: "/fulishemini/couponActivity/listActivityCoupon/distributionVenue",
  LIST_COUPON_WAIMAI: "/fulishemini/couponActivity/merchantCommissionVenuePreSend",
  // 领取优惠券
  GRANT_COUPON: "/fulishemini/couponActivity/grantActivityCoupon/distributionVenue",
  GRANT_COUPON_WAIMAI: "/fulishemini/couponActivity/merchantCommissionVenueSendCoupon",
  // 直接领券
  DIRECT_RECEIVE_COUPON: "/fulishemini/couponActivity/directGrantCoupon",
  // 韦恩供给召回判定
  CHECK_WAYNE_SUPPLY: "/fulishemini/couponSupply/checkWayneSupplyRecallable",
} as const;

// 导出路径常量供外部使用
export { COUPON_API_PATHS };

export enum CONTAINER_TYPE {
  MT_MINI = "mt_applet", // 美团小程序（美小）其他地方传的mt_mini，红包墙历史代码传 "mt_applet"
  MT_APP = "mt_app", // 美团App
  WX_BROWSER = "wx_webview", // 微信浏览器
  BROWSER = "browser", // 其他浏览器
}

/**
 * 运行环境到容器类型的映射
 * 用于将 RUNTIME_ENV_TYPE 转换为 CONTAINER_TYPE
 */
export const CONTAINER_TYPE_MAP: Record<string, CONTAINER_TYPE> = {
  [RUNTIME_ENV_TYPE.MT_MINI]: CONTAINER_TYPE.MT_MINI,
  [RUNTIME_ENV_TYPE.MT_APP]: CONTAINER_TYPE.MT_APP,
  [RUNTIME_ENV_TYPE.WX_BROWSER]: CONTAINER_TYPE.WX_BROWSER,
  [RUNTIME_ENV_TYPE.BROWSER]: CONTAINER_TYPE.BROWSER,
  [RUNTIME_ENV_TYPE.UNKNOWN]: CONTAINER_TYPE.BROWSER,
};

export interface ClientInfo {
  wm_uuid: string; // 设备ID
  wm_ctype?: string; // 客户端类型
  mtFingerprint?: string; // 指纹信息
  fp_platform: number; // 指纹平台标识
  wm_appversion: string; // 版本号
  city_id_level2: string; // 城市Id
  wm_actual_latitude: number; // 经度
  wm_actual_longitude: number; // 纬度
  appId?: string | null; // APP ID
  wm_dplatform: "ios" | "android" | "ohos"; // 平台标识
  open_id?: string; // 有就传，传空字符串会怎么样？
  expoId: string; // 加密的openId
  container: CONTAINER_TYPE; // 容器类型
}

export interface CouponWallRequestParams extends ClientInfo {
  requestType: number; // 0-默认，1-下拉刷新
  couponActivityScene: number; // 写死为1
  distributorChannel: number | null; // 分销会场固定传3，外卖会场传 null
}

/**
 * 获取券墙数据
 * 注意：如果接口返回的 code 不是 0，会被 request 拦截器 reject
 * 如果接口返回 401，需要在 catch 中处理
 */
export async function getCouponWall(
  params: CouponWallRequestParams,
  config: Record<string, unknown>,
): Promise<ResponseData<ActivityCouponResult>> {
  return mediaClient.post<CouponWallRequestParams, ActivityCouponResult>({
    url: COUPON_API_PATHS.LIST_COUPON,
    data: params,
    config: {
      ...config,
    },
  });
}

/**
 * 获取券墙数据（外卖出佣会场使用）
 * 注意：如果接口返回的 code 不是 0，会被 request 拦截器 reject
 * 如果接口返回 401，需要在 catch 中处理
 */
export async function getCouponWallForWaimai(
  params: CouponWallRequestParams,
  config: Record<string, unknown>,
): Promise<ResponseData<ActivityCouponResult>> {
  return mediaClient.post<CouponWallRequestParams, ActivityCouponResult>({
    url: COUPON_API_PATHS.LIST_COUPON_WAIMAI,
    data: params,
    config: {
      ...config,
    },
  });
}

/**
 * 领取券接口入参
 * */
export interface ReceiveCouponRequestParams extends ClientInfo {
  activityCode: string; // 活动编码，可选
  activityName?: string; // 活动名称
  activityScene: number; // 活动场景
  recallToken: string; // 召回token
  tabs: GrantRightsTab[];
  distributorChannel: number | null; // 分销渠道，固定传3，外卖会场传 null
  sessionId?: string; // 灵犀sessionId
  unpl?: string; // 跟单参数
  pageId: string; // 页面ID
  moduleId: string; // 模块ID
  preSendCouponList: GrantCouponItem[]; // 回传预发券列表，用于计算所有领取券的金额和数量
}

/**
 * 领取券接口
 * 注意：如果接口返回的 code 不是 0，会被 request 拦截器 reject
 * 如果接口返回 401，需要在 catch 中处理
 */
export async function receiveCoupon(
  params: ReceiveCouponRequestParams,
  config: Record<string, unknown>,
): Promise<ResponseData<ReceiveCouponData>> {
  return mediaClient.post<ReceiveCouponRequestParams, ReceiveCouponData>({
    url: COUPON_API_PATHS.GRANT_COUPON,
    data: params,
    config: {
      ...config,
    },
  });
}

/**
 * 领取券接口（外卖出佣会场使用）
 * 注意：如果接口返回的 code 不是 0，会被 request 拦截器 reject
 * 如果接口返回 401，需要在 catch 中处理
 */
export async function receiveCouponForWaimai(
  params: ReceiveCouponRequestParams,
  config: Record<string, unknown>,
): Promise<ResponseData<ReceiveCouponData>> {
  return mediaClient.post<ReceiveCouponRequestParams, ReceiveCouponData>({
    url: COUPON_API_PATHS.GRANT_COUPON_WAIMAI,
    data: params,
    config: {
      ...config,
    },
  });
}

/**
 * 检查韦恩供给召回判定接口入参
 */
export interface CheckWayneSupplyRecallableParams {
  src: string; // 来源， wm_fenxiao_center_page
  wayneActIdList: number[]; // 韦恩ID列表
  wm_ctype: string; // 客户端类型
  wm_actual_latitude: number; // 经度
  wm_actual_longitude: number; // 纬度
}

/**
 * 检查韦恩供给召回判定接口
 * 注意：如果接口返回的 code 不是 0，会被 request 拦截器 reject
 */
export async function checkWayneSupplyRecallable(
  params: CheckWayneSupplyRecallableParams,
  config: Record<string, unknown>,
): Promise<ResponseData<WayneSupplyRecallResult>> {
  return mediaClient.post<CheckWayneSupplyRecallableParams, WayneSupplyRecallResult>({
    url: COUPON_API_PATHS.CHECK_WAYNE_SUPPLY,
    data: params,
    config: {
      ...config,
    },
  });
}

/**
 * 直接领券接口入参
 */
export interface ReceiveCouponV2RequestParams extends ClientInfo {
  sessionId?: string; // 灵犀sessionId
  unpl?: string; // 跟单参数
  identityScene: IdentityScene; // 身份场景信息
}

export interface ReceiveCouponV2Data {
  currentTime: number; // 当前时间戳
  config: CouponActivityConfig; // 配置项
  totalGrantedCouponList: CouponWallItem[]; // 已发放的券列表
  totalCouponValue: number; // 成功领券总金额
  currentGrantedCount: number; // 成功领券数量
  title: TitleItem[];
  quickScreenCouponList: CouponData[];
}

/**
 * 新的领券接口，不需要传递预发券下发的数据
 */
export async function receiveCouponV2(
  params: ReceiveCouponV2RequestParams,
  config: Record<string, unknown>,
): Promise<ResponseData<ReceiveCouponV2Data>> {
  return mediaClient.post<ReceiveCouponV2RequestParams, ReceiveCouponV2Data>({
    url: COUPON_API_PATHS.DIRECT_RECEIVE_COUPON,
    data: params,
    config: {
      ...config,
    },
  });
}
/**
 * =====================================================
 * SSR兼容函数
 * =====================================================
 */

/**
 * SSR环境获取红包墙数据
 * @param requestParams 请求参数（包含ClientInfo和业务参数）
 * @param config SSR请求配置（可选，支持传递headers等）
 * @returns 优惠券活动完整响应
 */
export async function fetchCouponWallForSSR(
  requestParams: CouponWallRequestParams,
  config?: SSRFetchConfig,
): Promise<ActivityCouponResult> {
  const response = await ssrFetch.post<ActivityCouponResult, CouponWallRequestParams>(
    DomainType.MEDIA,
    COUPON_API_PATHS.LIST_COUPON,
    requestParams,
    config,
  );

  // 返回完整响应结构,与CSR保持一致
  return response?.data;
}

/**
 * SSR环境获取红包墙数据（外卖出佣会场使用）
 * @param requestParams 请求参数（包含ClientInfo和业务参数）
 * @param config SSR请求配置（可选，支持传递headers等）
 * @returns 优惠券活动完整响应
 */
export async function fetchCouponWallForWaimaiSSR(
  requestParams: CouponWallRequestParams,
  config?: SSRFetchConfig,
): Promise<ActivityCouponResult> {
  const response = await ssrFetch.post<ActivityCouponResult, CouponWallRequestParams>(
    DomainType.MEDIA,
    COUPON_API_PATHS.LIST_COUPON_WAIMAI,
    requestParams,
    config,
  );

  // 返回完整响应结构,与CSR保持一致
  return response?.data;
}
